/*
  Intentionally creating a manual record of team task types
  (rather than making a smart type that automatically knows about all defined task types)
  because there is not a 1-1 relationship (plus I tried out that route and it was painful to work with)
*/

import { z } from 'zod';

import { TeamTasksId } from '../BrandedIds';
import { DashboardTaskType } from '../tasks/Task';

export type TeamTaskType = Exclude<
  DashboardTaskType,
  'custom' | 'mailBundle' | 'workflowV2' | 'unappliedFundsReallocation'
>;

const LoanManagement = z.object({
  poolPendingActivation: z.boolean(),
  loanFlaggedTooLong: z.boolean(),
  welcomeCall: z.boolean(),
  partialLienRelease: z.boolean(),
  fullLienRelease: z.boolean(),
});

const BorrowerInquiry = z.object({
  generalInquiry: z.boolean(),
  drawRequest: z.boolean(),
  escrowWaiverRequest: z.boolean(),
  paymentAssistanceInquiry: z.boolean(),
  pmiCancellationRequest: z.boolean(),
  verificationOfMortgageRequest: z.boolean(),
});

const Documents = z.object({
  generateRequestedPayoff: z.boolean(),
  generatePayoffStatement: z.boolean(),
  generateManualStatement: z.boolean(),
  mailPreActiveWelcomeLetter: z.boolean(),
  mailMonthlyStatement: z.boolean(),
  mailGoodbyeLetter: z.boolean(),
  mailMortgageeLetter: z.boolean(),
  mail1098Form: z.boolean(),
  mailEscrowAnalysis: z.boolean(),
  borrowerSubmittedDocument: z.boolean(),
});

const Default = z.object({
  pastGrace: z.boolean(),
  applyDefaultInterest: z.boolean(),
  reviewDeferralOfferPayment: z.boolean(),
});

const Disbursements = z.object({
  forwardMiscollectedFunds: z.boolean(),
  disburseEscrowFunds: z.boolean(), // This applies to both escrowed and non-escrowed T&I according to company task settings
  disburseEscrowFundsToBorrower: z.boolean(),
  payoffReimbursement: z.boolean(),
  pendingPaymentAtPayoffReimbursement: z.boolean(),
  disableUsioAccount: z.boolean(),
  disableRepayAccount: z.boolean(),
  achPaymentFailed: z.boolean(),
});

// TODO followup delete from defintion
const DelinquencyWorkflow = z.object({
  // Foreclosure steps
  foreclosureStepDemandLetters: z.boolean().optional(),
  foreclosureStepDemandLetterExpires: z.boolean().optional(),
  foreclosureStepReferClosureAttorney: z.boolean().optional(),
  foreclosureStepJudicialState: z.boolean().optional(),
  foreclosureStepNonJudicialState: z.boolean().optional(),
  foreclosureStepHomeSale: z.boolean().optional(),

  // REO steps
  reoStepContractStatus: z.boolean().optional(),
  reoStepCounterOfferDetails: z.boolean().optional(),
  reoStepEvictionOfOccupiedProperty: z.boolean().optional(),
  reoStepOfferEvaluations: z.boolean().optional(),
  reoStepOrderInspectionAndValuation: z.boolean().optional(),
  reoStepPropertyListing: z.boolean().optional(),
  reoStepPropertyReduction: z.boolean().optional(),
  reoStepRepairsAndPreservation: z.boolean().optional(),

  // Chapter 13 steps
  chapter13StepAttorneyReferral: z.boolean().optional(),
  chapter13StepCreditorMeeting: z.boolean().optional(),
  chapter13StepFilingStay: z.boolean().optional(),
  chapter13StepPlanInPlace: z.boolean().optional(),
  chapter13StepPlanMonitoring: z.boolean().optional(),

  // Chapter 7 steps
  chapter7StepAttorneyRelief: z.boolean().optional(),
  chapter7StepCreditorMeeting: z.boolean().optional(),
  chapter7StepFilingStay: z.boolean().optional(),
  chapter7StepHearingDate: z.boolean().optional(),
  chapter7StepHearingOutcome: z.boolean().optional(),

  // Delinquency steps
  delinquencyStepPastGrace: z.boolean().optional(),
  delinquencyStepMortgageAssistanceRequest: z.boolean().optional(),
  delinquencyStepBorrowerResponsePackage: z.boolean().optional(),
  delinquencyStepFlexModificationEligibility: z.boolean().optional(),
  delinquencyStepFlexModificationTerms: z.boolean().optional(),
  delinquencyStepSendBreachTerms: z.boolean().optional(),
  delinquencyStepPreForeclosureReferral: z.boolean().optional(),
});

export const TeamTaskSettings = z.object({
  ...LoanManagement.shape,
  ...BorrowerInquiry.shape,
  ...Documents.shape,
  ...Default.shape,
  ...Disbursements.shape,
  ...DelinquencyWorkflow.shape,
}) satisfies z.ZodType<Record<TeamTaskType, boolean>>;

export type TeamTaskSettings = z.infer<typeof TeamTaskSettings>;

export const TeamTasks = z.object({
  id: TeamTasksId,
  settings: TeamTaskSettings,
});
export type TeamTasks = z.infer<typeof TeamTasks>;

/* HELPERS */
export const getDefaultTeamTaskSettings = (defaultValue = false): TeamTaskSettings => ({
  // Loan Management
  poolPendingActivation: defaultValue,
  loanFlaggedTooLong: defaultValue,
  welcomeCall: defaultValue,
  partialLienRelease: defaultValue,
  fullLienRelease: defaultValue,

  // Borrower Inquiry
  generalInquiry: defaultValue,
  drawRequest: defaultValue,
  escrowWaiverRequest: defaultValue,
  paymentAssistanceInquiry: defaultValue,
  pmiCancellationRequest: defaultValue,
  verificationOfMortgageRequest: defaultValue,

  // Documents
  generateRequestedPayoff: defaultValue,
  generatePayoffStatement: defaultValue,
  generateManualStatement: defaultValue,
  mailPreActiveWelcomeLetter: defaultValue,
  mailMonthlyStatement: defaultValue,
  mailGoodbyeLetter: defaultValue,
  mailMortgageeLetter: defaultValue,
  mail1098Form: defaultValue,
  mailEscrowAnalysis: defaultValue,
  borrowerSubmittedDocument: defaultValue,

  // Default
  pastGrace: defaultValue,
  applyDefaultInterest: defaultValue,
  reviewDeferralOfferPayment: defaultValue,

  // Disbursements
  forwardMiscollectedFunds: defaultValue,
  disburseEscrowFunds: defaultValue,
  disburseEscrowFundsToBorrower: defaultValue,
  payoffReimbursement: defaultValue,
  pendingPaymentAtPayoffReimbursement: defaultValue,
  disableUsioAccount: defaultValue,
  disableRepayAccount: defaultValue,
  achPaymentFailed: defaultValue,
});

export const DEFAULT_TEAM_TASK_SETTINGS = getDefaultTeamTaskSettings(false);
